import { Icon } from "../components/Icon";
import { Kpi } from "../components/Kpi";
import { PageHeader } from "../components/PageHeader";
import { EmptyState, ErrorState, SkeletonLines } from "../components/States";
import { BRAND, hasModule } from "../lib/config";
import { newLeads } from "../lib/data";
import { audWhole, fmtLongDay } from "../lib/format";
import { leadSource, leadSummary } from "../lib/leads";
import { useAsync } from "../lib/useAsync";
import { useFieldToday } from "../modules/field-today";
import { WhatsNewCard } from "../help/Help";
import { openRequests } from "../lib/customers-data";
import { fmtDate } from "../lib/format";
import { REQUEST_LABEL, REQUEST_TONE, UrgencyTag } from "./CustomerServices";

const showRequests = () => hasModule("customers") && __OPS_PORTAL__.customerRequests;

/** Home screen: new leads, plus whatever the client's modules add (see modules/field-today). */
export function Today() {
  const leads = useAsync(() => newLeads(5));
  const field = useFieldToday();
  const showEstimates = BRAND.instantQuote && hasModule("quotes");
  const requests = useAsync(() => (showRequests() ? openRequests() : Promise.resolve(undefined)));
  const waiting = requests.data?.filter((r) => r.status === "received").length;

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader eyebrow={fmtLongDay(new Date())} title="Today" actions={field.actions} />

      <WhatsNewCard />

      <WhatsNewCard />

      <div className="kpis">
        {field.kpisBefore}
        <Kpi label="New leads" value={leads.data?.count} loading={leads.loading} failed={!!leads.error}
          sub={leads.data ? "Waiting for first contact" : undefined} href="#/leads" />
        {showRequests() && (
          <Kpi label="Customer requests" value={waiting} loading={requests.loading} failed={!!requests.error}
            sub={requests.data ? `${requests.data.length} open, ${waiting} not answered yet` : undefined} href="#/customers" />
        )}
        {field.kpisAfter}
      </div>

      <div className={field.main ? "today-grid" : undefined}>
        {field.main}

        <div className={field.main ? "today-grid__side" : undefined}>
          <div className="card">
            <div className="card__head">
              <h2 className="card__title">New leads</h2>
              <a className="card__link" href="#/leads">All leads <Icon name="arrow" size={16} /></a>
            </div>
            {leads.loading && <SkeletonLines rows={4} />}
            {leads.error && <ErrorState message={leads.error} onRetry={leads.reload} />}
            {leads.data && leads.data.items.length === 0 && <EmptyState help icon="inbox" title="Inbox clear">No leads waiting for contact.</EmptyState>}
            {leads.data && leads.data.items.length > 0 && (
              <ul className="list">
                {leads.data.items.map((l) => (
                  <li key={l.id} className="list__row">
                    <span>
                      <a href={`#/leads/${l.id}`}><strong>{l.name}</strong></a>, {leadSource(l.source).toLowerCase()}
                      <span className="sub">{leadSummary(l)}</span>
                    </span>
                    {l.estimate_result?.total !== undefined
                      ? <span className="list__value">{audWhole(l.estimate_result.total)}</span>
                      : showEstimates && <span className="list__value list__value--muted">No estimate</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {showRequests() && (
            <div className="card today-requests">
              <div className="card__head">
                <h2 className="card__title">Customer requests</h2>
                <a className="card__link" href="#/customers">Customers <Icon name="arrow" size={16} /></a>
              </div>
              {requests.loading && <SkeletonLines rows={3} />}
              {requests.error && <ErrorState message={requests.error} onRetry={requests.reload} />}
              {requests.data && requests.data.length === 0 && <EmptyState icon="inbox" title="No open requests">Requests from customers' portals show here.</EmptyState>}
              {requests.data && requests.data.length > 0 && (
                <ul className="list">
                  {requests.data.slice(0, 8).map((r) => (
                    <li key={r.id} className="list__row">
                      <span>
                        <a href={`#/customers/${r.customer_id}`}><strong>{r.title}</strong></a>
                        <UrgencyTag urgency={r.urgency} />
                        <span className="sub">{r.customer}, {r.reference}, {fmtDate(r.created_at)}</span>
                      </span>
                      <span className={`chip chip--${REQUEST_TONE[r.status]}`}>{REQUEST_LABEL[r.status]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {field.side}
        </div>
      </div>
    </section>
  );
}
